import { Box, Slider, styled } from '@mui/material';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useMaxPriceSWR from '../../../hooks/collection/useMaxPriceSWR';
import { CollectionFilterProps } from '../../../models/CollectionFilter';
import TextFieldStyled from '../../shared/TextFieldStyled';
import { FilterAccordion } from '../../styled/StyledAccordion';

const StyledPriceBoxWrapper = styled(Box)(() => ({
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    overflowX: 'hidden',
}));

export const CollectionPriceFilter: React.FC<CollectionFilterProps> = (
    props
) => {
    const { handleFilter, collectionId, search } = props;

    const { maxPrice, isLoading } = useMaxPriceSWR({
        collectionIds: collectionId ? [collectionId] : [],
        search: search,
    });

    const { t } = useTranslation('collection', {
        keyPrefix: 'COLLECTIONS.FILTER_ITEM',
    });
    const [value, setValue] = React.useState<number[]>([0, 1000000000]);

    const handleRangeChange = (event: Event, newValue: number | number[]) => {
        setValue(newValue as number[]);
    };

    const getNumberOrZero = (numberString: string): number => {
        const parsed = parseFloat(numberString);
        return isNaN(parsed) ? 0 : parsed;
    };

    const handleLowerBoundChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const minValue = Math.max(0, getNumberOrZero(event.target.value));

        if (minValue >= value[1]) {
            setValue([value[1] - 1, value[1]]);
            return;
        }

        setValue([minValue, value[1]]);
    };

    const handleUpperBoundChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const maxValue = Math.min(
            parseFloat(maxPrice),
            getNumberOrZero(event.target.value)
        );

        if (maxValue <= value[0]) {
            setValue([value[0], value[0] + 1]);
            return;
        }

        setValue([value[0], maxValue]);
    };

    useEffect(() => {
        if (!isLoading && maxPrice) {
            setValue([0, Math.ceil(Number(maxPrice))]);
        }
    }, [isLoading, maxPrice]);

    useEffect((): void => {
        handleFilter(value);
    }, [value]);

    return (
        <>
            {!isLoading && (
                <div>
                    <FilterAccordion title={t('PRICE')}>
                        <div>
                            <Slider
                                value={value}
                                onChange={handleRangeChange}
                                min={0}
                                max={Math.ceil(Number(maxPrice))}
                            />
                        </div>
                        <StyledPriceBoxWrapper>
                            <div>
                                <TextFieldStyled
                                    value={value[0]}
                                    onChange={handleLowerBoundChange}
                                    InputProps={{
                                        onContextMenuCapture: (event) =>
                                            event.preventDefault(),
                                        onFocus: (event) => {
                                            event.target.select();
                                        },
                                    }}
                                />
                            </div>
                            <div>-</div>
                            <div>
                                <TextFieldStyled
                                    value={value[1]}
                                    onChange={handleUpperBoundChange}
                                    InputProps={{
                                        onContextMenuCapture: (event) =>
                                            event.preventDefault(),
                                        onFocus: (event) => {
                                            event.target.select();
                                        },
                                    }}
                                />
                            </div>
                        </StyledPriceBoxWrapper>
                    </FilterAccordion>
                </div>
            )}
        </>
    );
};
